import { createApi } from '@reduxjs/toolkit/query/react';

import { API_CONSTANTS } from '../utils/constants';

import type { AppFilterPayload, ApiResponse, ListResponse } from '../model';
import type { StatusChangePayload } from './model';
import type { Page, StaticPage } from '../modules/content/staticPage/model';
import type { Country, State, SingleState } from '../modules/region/model';
import { baseQueryWithReauth } from './baseQuery';

export const toolsApi = createApi({
  reducerPath: 'toolsApi',
  tagTypes: ['Countries', 'FilterCountry', 'States', 'Pages', 'Cities'],
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    fetchCountriesByFilter: builder.query<
      ListResponse<Country>,
      AppFilterPayload
    >({
      query: (payload) => ({
        url: API_CONSTANTS.COUNTRIES_ENDPOINT,
        method: 'POST',
        body: payload,
      }),
      providesTags: () => [{ type: 'FilterCountry', id: 'LIST' }],
    }),
    fetchCountries: builder.query<ListResponse<Country>, void>({
      query: () => ({
        url: API_CONSTANTS.COUNTRIES_AUTOCOMPLETE_ENDPOINT,
        method: 'GET',
      }),
      providesTags: ['Countries'],
    }),

    getCountryById: builder.query<{ data: Country }, string>({
      query: (countryId) =>
        `${API_CONSTANTS.COUNTRIES_GET_ENDPOINT}/${countryId}`,
    }),
    addCountry: builder.mutation<ApiResponse<object>, object>({
      query: (formData) => ({
        url: API_CONSTANTS.COUNTRIES_ADD_ENDPOINT,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['FilterCountry'],
    }),
    updateCountry: builder.mutation<
      ApiResponse<object>,
      { formData: object; countryId: string }
    >({
      query: ({ formData, countryId }) => ({
        url: `${API_CONSTANTS.COUNTRIES_GET_ENDPOINT}/${countryId}`,
        method: 'PUT',
        body: formData,
      }),
      invalidatesTags: ['FilterCountry'],
    }),
    changeCountryStatus: builder.mutation<
      ApiResponse<Country>,
      StatusChangePayload
    >({
      query: ({ ids, status }) => ({
        url: API_CONSTANTS.COUNTRIES_CHANGE_STATUS_ENDPOINT,
        method: 'POST',
        body: { ids, status },
      }),
    }),
    deleteCountry: builder.mutation<ApiResponse<object>, string>({
      query: (countryId) => ({
        url: `${API_CONSTANTS.COUNTRIES_GET_ENDPOINT}/${countryId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FilterCountry'],
    }),
    fetchStatesByFilter: builder.query<ListResponse<State>, AppFilterPayload>({
      query: (payload) => ({
        url: API_CONSTANTS.STATES_ENDPOINT,
        method: 'POST',
        body: payload,
      }),
      providesTags: (payload) => [{ type: 'States', ...payload }],
    }),
    getStateById: builder.query<ApiResponse<SingleState>, string>({
      query: (stateId: string) =>
        `${API_CONSTANTS.STATES_GET_ENDPOINT}/${stateId}`,
    }),
    addState: builder.mutation<ApiResponse<object>, unknown>({
      query: (formData: unknown) => ({
        url: API_CONSTANTS.STATES_ADD_ENDPOINT,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: () => [{ type: 'States' }],
    }),
    updateState: builder.mutation<
      ApiResponse<object>,
      { formData: object; stateId: string }
    >({
      query: ({
        formData,
        stateId,
      }: {
        formData: object;
        stateId: string;
      }) => ({
        url: `${API_CONSTANTS.STATES_EDIT_ENDPOINT}/${stateId}`,
        method: 'PUT',
        body: formData,
      }),
      invalidatesTags: () => [{ type: 'States' }],
    }),
    deleteState: builder.mutation<ApiResponse<object>, string>({
      query: (stateId) => ({
        url: `${API_CONSTANTS.STATES_GET_ENDPOINT}/${stateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['States'],
    }),
    changeStateStatus: builder.mutation<
      ApiResponse<Country>,
      StatusChangePayload
    >({
      query: ({ ids, status }) => ({
        url: API_CONSTANTS.STATES_CHANGE_STATUS_ENDPOINT,
        method: 'POST',
        body: { ids, status },
      }),
    }),
    changeCityStatus: builder.mutation<
      ApiResponse<Country>,
      StatusChangePayload
    >({
      query: ({ ids, status }) => ({
        url: API_CONSTANTS.CITIES_CHANGE_STATUS_ENDPOINT,
        method: 'POST',
        body: { ids, status },
      }),
    }),
    getCityById: builder.query<ApiResponse<any>, string>({
      query: (cityId: string) =>
        `${API_CONSTANTS.CITIES_GET_ENDPOINT}/${cityId}`,
    }),
    addCity: builder.mutation<ApiResponse<object>, unknown>({
      query: (formData: unknown) => ({
        url: API_CONSTANTS.CITIES_ADD_ENDPOINT,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: () => [{ type: 'Cities' }],
    }),
    updateCity: builder.mutation<
      ApiResponse<object>,
      { formData: object; cityId: string }
    >({
      query: ({ formData, cityId }: { formData: object; cityId: string }) => ({
        url: `${API_CONSTANTS.CITIES_EDIT_ENDPOINT}/${cityId}`,
        method: 'PUT',
        body: formData,
      }),
      invalidatesTags: () => [{ type: 'Cities' }],
    }),
    deleteCity: builder.mutation<ApiResponse<object>, string>({
      query: (cityId) => ({
        url: `${API_CONSTANTS.CITIES_GET_ENDPOINT}/${cityId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Cities'],
    }),
    fetchStaticPagesByFilter: builder.query<
      ListResponse<StaticPage>,
      AppFilterPayload
    >({
      query: (payload) => ({
        url: 'api/v1/static-page/list',
        method: 'POST',
        body: payload,
      }),
      providesTags: (payload) => [{ type: 'Pages', ...payload }],
    }),
    getPageById: builder.query<ApiResponse<Page>, string>({
      query: (pageId: string) => `api/v1/static-page/${pageId}`,
    }),
    addPage: builder.mutation<ApiResponse<object>, unknown>({
      query: (formData: unknown) => ({
        url: 'api/v1/static-page',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: () => [{ type: 'Pages' }],
    }),
    fetchRegionStates: builder.query<
      ApiResponse<Array<{ stateId: number; state: string }>>,
      { countryId?: number; stateId?: number; keyword?: string }
    >({
      query: (params) => ({
        url: 'api/v1/state/country-states',
        method: 'GET',
        params,
      }),
    }),
    fetchCities: builder.query<
      ApiResponse<Array<{ cityId: number; city: string }>>,
      { countryId?: number; stateId?: number; keyword?: string }
    >({
      query: (params) => ({
        url: 'api/v1/city/autocomplete',
        method: 'GET',
        params,
      }),
    }),
    updatePage: builder.mutation<
      ApiResponse<object>,
      { formData: object; pageId: string }
    >({
      query: ({ formData, pageId }: { formData: object; pageId: string }) => ({
        url: `api/v1/static-page/${pageId}`,
        method: 'PUT',
        body: formData,
      }),
      invalidatesTags: () => [{ type: 'Pages' }],
    }),
  }),
});

export const {
  useFetchCountriesQuery,
  useFetchCountriesByFilterQuery,
  useChangeCountryStatusMutation,
  useDeleteCountryMutation,
  useGetCountryByIdQuery,
  useLazyGetCountryByIdQuery,
  useAddCountryMutation,
  useUpdateCountryMutation,
  useFetchStatesByFilterQuery,
  useAddStateMutation,
  useGetStateByIdQuery,
  useUpdateStateMutation,
  useDeleteStateMutation,
  useChangeStateStatusMutation,
  useChangeCityStatusMutation,
  useFetchStaticPagesByFilterQuery,
  useAddPageMutation,
  useUpdatePageMutation,
  useGetPageByIdQuery,
  useFetchRegionStatesQuery,
  useFetchCitiesQuery,
  useGetCityByIdQuery,
  useAddCityMutation,
  useUpdateCityMutation,
  useDeleteCityMutation,
} = toolsApi;
export default toolsApi;
